<script setup lang="ts">
import { create_fleet } from '@/api/calls/backend-calls/fleet-calls'
import { createCustomerAndGetSubscriptionPage } from '@/api/subscriptions/index.ts'
import { keycloak } from '@/main.ts'
import { ActionTypes, type State } from '@/store/store_types'
import { onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '../ui-elements/buttons/OutlinedBtn.vue'
import ErrorCard from './ErrorCard.vue'
import ProcessingCard from './ProcessingCard.vue'
import SuccessCard from './SuccessCard.vue'

/**
 * Need to develop this more to include, a stripe integration to allow creation of a fleet.
 * looks like it will be an endpoint that will be called to retrieve a stripe checkout session link.
 */

const store = useStore<State>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

type View =
  | 'LOADING'
  | 'SUBSCRIPTION'
  | 'CREATE'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'ERROR'
  | 'SUBSCRIPTION_ERROR'

const view = ref<View>('LOADING')

// const status = ref<'processing' | 'success' | 'error' | null>(null)
const dirty = ref(false)
const fleetName = ref('')
const validationError = ref<string | null>(null)

const recheckStatusAndRefresh = async () => {
  await store.state.permissions?.refresh()
  store.dispatch(ActionTypes.refetchData)
}

const validateFleet = () => {
  if (!fleetName.value.trim()) {
    validationError.value = 'Fleet name is required'
    return false
  }

  validationError.value = null
  return true
}

const submit = async () => {
  // validation
  if (!validateFleet()) return

  view.value = 'PROCESSING'
  
  // submit create fleet API call
  try {
    const response = await create_fleet(fleetName.value.trim())
    view.value = response ? 'SUCCESS' : 'ERROR'
  } catch (err) {
    view.value = 'ERROR'
  }
}

const subscriptionError = ref<string | null>(null)

onMounted(() => {  
  const isSubscriber =
    keycloak.tokenParsed?.realm_access?.roles?.includes('subscribed')
  if (isSubscriber) view.value = 'CREATE'
  else view.value = 'SUBSCRIPTION'
})

const toSubscribe = async () => {
  view.value = 'LOADING'
  subscriptionError.value = null
  const res = await createCustomerAndGetSubscriptionPage()
  if (res.success !== 'OK') {
    subscriptionError.value = res.error
    view.value = 'SUBSCRIPTION_ERROR'
  } else {
    window.location.href = res.sessionURL
  }
}
</script>
<template>
  <ProcessingCard v-if="view === 'LOADING'" title="loading" />

  <ErrorCard
    v-else-if="view === 'SUBSCRIPTION_ERROR'"
    title="Subscription error"
    :message="`${subscriptionError}. Please try again.`"
    actionName="Try Again"
    :action="() => toSubscribe()"
    @close="subscriptionError = null"
  />

  <ProcessingCard
    v-else-if="view === 'PROCESSING'"
    title="Creating Fleet"
    message="Please wait while we process your request..."
  />

  <ErrorCard
    v-else-if="view === 'ERROR'"
    title="Failed to Create Fleet"
    message="There was an issue creating the fleet. Please try again."
    actionName="Try Again"
    :action="() => submit()"
    @close="emit('close')"
  />

  <SuccessCard
    v-else-if="view === 'SUCCESS'"
    title="Successfully Created Fleet"
    message="You have successfully created the fleet! You can now access fleet features."
    @close="recheckStatusAndRefresh"
  />

  <v-card v-else-if="view === 'SUBSCRIPTION'" class="rounded-lg pa-6">
    <v-card-title class="d-flex align-center">
      <v-btn icon @click="emit('close')" flat density="comfortable" color="transparent">
        <v-icon color="primary">mdi-chevron-left</v-icon>
      </v-btn>
      <v-spacer />
      Subscribe to Power Trip essentials
      <v-spacer />
    </v-card-title>
    <v-card-text class="text-center">
      Power Trip essentials requires a subscription to create a fleet, clicking continue will
      redirect you to our secured payment gateway to complete your subscription.
    </v-card-text>
    <v-card-actions class="justify-center">
      <OutlinedBtn @click="emit('close')" shape="box" class="mr-2"> Cancel </OutlinedBtn>
      <ElevatedBtn @click="toSubscribe" shape="box" class="ml-2"> Continue </ElevatedBtn>
    </v-card-actions>
  </v-card>

  <v-card v-else class="rounded-lg pa-6">
    <v-card-title class="d-flex align-center">
      <v-btn icon @click="emit('close')" flat density="comfortable" color="transparent">
        <v-icon color="primary">mdi-chevron-left</v-icon>
      </v-btn>
      <v-spacer />
      Create a new fleet
      <v-spacer />
    </v-card-title>
    <v-card-text class="text-center">
      <v-text-field
        label="Fleet name"
        variant="outlined"
        class="mt-4"
        v-model="fleetName"
        @input="dirty = true"
        :error-messages="validationError"
      ></v-text-field>
    </v-card-text>
    <v-card-actions class="justify-center">
      <OutlinedBtn @click="emit('close')" shape="box" class="mr-2"> Cancel </OutlinedBtn>
      <ElevatedBtn @click="submit" shape="box" class="ml-2" :disabled="!dirty || !fleetName">
        Continue
      </ElevatedBtn>
    </v-card-actions>
  </v-card>
</template>
